import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import { ChangePassword } from "../../components/change-password";
import { Modal } from "../../components/modal";

import type { ApiError, VerifyUserRequest } from "@sperrdatei/dto/types";

export const UserVerify: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | undefined>();
  const [userId, setUserId] = useState<string | undefined>();
  const [organizationId, setOrganizationId] = useState<string | undefined>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hasAcceptedTermsOfService, setHasAcceptedTermsOfService] =
    useState(false);
  const [verificationCode, setVerfificationCode] = useState<
    string | undefined
  >();

  useEffect(() => {
    const userIdParam = searchParams.get("userId");
    const organizationIdParam = searchParams.get("organizationId");
    const verificationCodeParam = searchParams.get("verificationCode");

    if (!userIdParam || !organizationIdParam || !verificationCodeParam) {
      setError(
        "Der Link ist fehlerhaft. Bitte nutzen Sie den Link aus der E-Mail",
      );
      return;
    }

    setUserId(userIdParam);
    setOrganizationId(organizationIdParam);
    setVerfificationCode(verificationCodeParam);
  }, [searchParams]);

  const handleSubmit = async (
    password: string,
    termsAccepted: boolean,
  ): Promise<ApiError | undefined> => {
    if (!userId || !organizationId || !verificationCode) {
      return;
    }

    const verifyUserRequest: VerifyUserRequest = {
      userId,
      organizationId,
      verificationCode,
      password,
      hasAcceptedTermsOfService: termsAccepted || false,
    };

    const response = await fetch("/api/user/verify", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(verifyUserRequest),
    });

    if (!response.ok) {
      const error = (await response.json()) as ApiError;
      if (error.code === "TERMS_NOT_ACCEPTED") {
        return {
          code: error.code,
          message: "Sie müssen die Datenschutzerklärung akzeptieren.",
        };
      }

      return error;
    }

    return undefined;
  };

  return error ? (
    <div className="container mt-2">
      <div className="alert alert-danger" role="alert">
        <h4 className="alert-heading">
          <i className="bi bi-exclamation-triangle-fill"></i> Fehler
        </h4>
        <p className="mb-0">{error}</p>
      </div>
    </div>
  ) : (
    <>
      <Modal
        isOpen={isModalOpen}
        className="modal-lg"
        onClose={() => setIsModalOpen(false)}
        title="Datenschutzerklärung"
        primaryAction={{
          label: "Annehmen",
          buttonStyle: "primary",
          showLoadingIndicator: false,
          onClick: async () => {
            setIsModalOpen(false);
            setHasAcceptedTermsOfService(true);
          },
        }}
        secondaryAction={{
          label: "Ablehnen",
          onClick: () => {
            setIsModalOpen(false);
            setHasAcceptedTermsOfService(false);
          },
        }}
      >
        <div>
          <h4>ADM - Sperrdatei</h4>
          <h4>Datenschutzerklärung</h4>
          <b>A. Vorbemerkung</b>
          <p>
            Sowohl bei der Programmierung und informationstechnischen
            Organisation der „Sperrdatei“ als auch bei ihrer
            forschungsmethodischen Nutzung müssen verschiedene
            datenschutzrechtliche Aspekte berücksichtigt bzw. beachtet werden,
            auf die im Folgenden jeweils näher eingegangen wird.
          </p>
          <b>
            B. Verantwortlicher im Sinne der DSGVO und Kontaktdaten des
            Datenschutzbeauftragten
          </b>
          <p>
            <b>
              ADM Arbeitskreis Deutscher Markt- und Sozialforschungsinstitute
              e.V.
            </b>
            , Französische Straße 8, 10117 Berlin, Telefon: 030 2061638-0,
            E-Mail: office@adm-ev.de, Internet: www.adm-ev.de <br />
            <br />
            Datenschutzbeauftrage des ADM Frau Andrea Schweizer (c/o Kanzlei
            Prof. Schweizer Rechtsanwaltsgesellschaft mbH, Schneckenburgerstraße
            22, 81675 München, Kontaktdaten: 089/92808525, as@schweizer.eu)
          </p>
          <b>C. Zweckbestimmung und Rechtsgrundlage </b>
          <p>
            Das Erheben, Verarbeiten und Nutzen der in der „Sperrdatei“
            enthaltenen personenbezogenen Daten (nur Telefonnummern, ohne Namen
            o.ä.) dient ausschließlich dem Zweck zu verhindern, dass angerufene
            Personen und Unternehmen, die deutlich zu erkennen geben, dass sie
            nicht angerufen werden wollen, weil sie generell an telefonischen
            Befragungen zu Zwecken der Markt-, Meinungs- und Sozialforschung
            nicht teilnehmen, von den Mitgliedsinstituten des ADM unter dieser
            und gegebenenfalls anderen Telefonnummern nochmals angerufen werden.
            Dabei spielt die Form, in der dieses Verlangen vorgebracht wird,
            keine Rolle.
          </p>
          <p>Rechtsgrundlage:</p>
          <ul>
            <li>Art. 6 Abs. 1 lit. a DSGVO: Einwilligung der Betroffenen</li>
            <li>
              Soweit eine Einwilligung nicht eingeholt werden kann - Art. 6 Abs.
              1 lit. f DSGVO (Art. 21 Abs. 1, Art. 17 Abs. 3 lit. b DSGVO):
              berechtigtes Interesse (Schutz vor unerwünschten Anrufen,
              Einhaltung der Unterlassungsverpflichtung/des Widerspruchs)
            </li>
          </ul>
          <b>D. Datensparsamkeit, Speicherdauer</b>
          <p>
            Den Grundsätzen der Datensparsamkeit und Datenvermeidung gemäß
            Artikel 5 DSGVO entsprechend, werden in der „Sperrdatei“ lediglich
            Telefonnummern, ein Merkmal der Zuordnung als privat oder
            geschäftlich genutzte Nummer, das Datum der Meldung in die
            „Sperrdatei“ sowie (temporär) der Name des meldenden Instituts
            gespeichert. Der Name des jeweils meldenden Instituts ist nur der
            die „Sperrdatei“ im Auftrag des ADM führenden Stelle bekannt, um
            gegebenenfalls Rückfragen oder Unstimmigkeiten zu prüfen. Eine
            Verarbeitung und Nutzung weiterer personenbezogener Daten ist für
            den Zweck der „Sperrdatei“ nicht notwendig. Die Telefonnummern
            werden gespeichert bis der Speicherung widersprochen oder die
            Löschung verlangt wird.
          </p>
          <b>E. Fallgruppen der Sperrung und der Benachrichtigung</b>
          <p>
            <b>Fall 1:</b> Eine angerufene Person verlangt gegenüber dem
            Institut oder dem ADM schriftlich (per Briefpost, Telefax oder
            E-Mail), nicht mehr angerufen zu werden, und/oder die Sperrung der
            Telefonnummer(n). In diesem Fall wird sie schriftlich (vorzugsweise
            auf demselben Kommunikationskanal) über die Sperrung und deren Zweck
            informiert.
          </p>
          <p>
            <b>Fall 2:</b> Eine angerufene Person verlangt gegenüber dem
            Interviewer mündlich, nicht mehr angerufen zu werden, und/oder die
            Sperrung der Telefonnummer(n) und teilt ihren Namen und Anschrift
            mit. In diesem Fall wird sie vom Interviewer mündlich und
            anschließend vom Institut schriftlich (per Briefpost, Telefax oder
            E-Mail) über die Sperrung und deren Zweck benachrichtigt.
          </p>
          <p>
            <b>Fall 3:</b> Eine angerufene Person verlangt gegenüber dem
            Interviewer mündlich, nicht mehr angerufen zu werden, und/oder die
            Sperrung der Telefonnummer, teilt aber nicht ihren Namen und ihre
            Anschrift mit. In diesem Fall wird sie vom Interviewer mündlich über
            die Sperrung und deren Zweck benachrichtigt.
          </p>
          <p>
            <b>Fall 4:</b> Eine angerufene Person verlangt gegenüber dem
            Interviewer mündlich, nicht mehr angerufen zu werden. Sie gibt dem
            Interviewer aber keine Möglichkeit (zumeist durch sofortiges Beenden
            des Gesprächs), über die Sperrung der Telefonnummer zu informieren.
            In diesem Fall kann über die Sperrung und deren Zweck nicht
            benachrichtigt werden.
          </p>
          <p>
            Soweit es möglich ist und sinnvoll erscheint, sind die Betroffenen
            auf eine der jeweiligen Fallgruppe angemessenen Weise nach eventuell
            weiteren zu sperrenden Telefonnummern zu ADM - Sperrdatei für
            Telefonstichproben 30.12.2025 Seite 3 fragen. Personen oder
            Unternehmen, die ohne angerufen zu werden gegenüber einem Institut
            „pro-aktiv“ die Sperrung ihrer Telefonnummer(n) verlangen, können
            diesbezüglich an den ADM verwiesen werden.
          </p>
          <b>F. Rechte der Betroffenen </b>
          <p>
            Die Betroffenen(Inhaber der Telefonnummern) haben folgende Rechte:
          </p>
          <ul>
            <li>Recht auf Auskunft (Art. 15 DSGVO)</li>
            <li>Recht auf Berichtigung (Art. 16 DSGVO)</li>
            <li>
              Recht auf Löschung, „Recht auf Vergessenwerden“ (Art. 17 DSGVO) –
              mit dem Hinweis an den Betroffenen, dass damit nicht
              sichergestellt werden kann, dass zukünftig nicht mehr angerufen
              wird
            </li>
            <li>Recht auf Einschränkung der Verarbeitung (Art. 18 DSGVO)</li>
            <li>Recht auf Datenübertragbarkeit (Art. 20 DSGVO)</li>
            <li>
              Recht auf Widerspruch gegen die Verarbeitung (Art. 21 DSGVO)
            </li>
            <li>
              Recht, sich bei einer Datenschutz-Aufsichtsbehörde über die
              Verarbeitung der personenbezogenen Daten durch den
              Verantwortlichen zu beschweren (Art. 77 DSGVO)
            </li>
          </ul>
          <b>G. Datensicherheit</b>
          <p>
            Alle die „Sperrdatei“ verwendenden Mitgliedsinstitute des ADM und
            die sie im Auftrag des ADM führende Stelle sowie der ADM selbst als
            für die „Sperrdatei“ verantwortliche Stelle müssen geeignete
            organisatorische und technische Maßnahmen zur Sicherheit der in der
            „Sperrdatei“ enthaltenen Daten treffen.
          </p>
          <b>H. Auftragsverarbeitung</b>
          <p>
            Die Sperrdatei wird vom technischen Dienstleister BIK ASCHPURWIS +
            BEHRENS GMBH, Krähenweg 28, 22459 Hamburg als Auftragsverarbeiter
            (Art. 28 DSGVO) verwaltet. Das Hosting erfolgt innerhalb der EU bei
            UpCloud Oy (Aleksanterinkatu 15 B, 7th floor, 00100 Helsinki,
            Finland).
          </p>
        </div>
      </Modal>

      <ChangePassword
        successTitle="Registrierung abgeschlossen"
        successMessage="Sie können sich jetzt mit ihrer E-Mail-Adresse und Passwort"
        termsOfServiceAccepted={hasAcceptedTermsOfService}
        openTermsOfService={() => setIsModalOpen(true)}
        submitAction={(password) => {
          return handleSubmit(password, hasAcceptedTermsOfService);
        }}
      />
    </>
  );
};
